import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import type { Profile } from "../../domain/entities/Profile";

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: Profile;
  onSave: (data: Profile) => void;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
}

export const EditProfileDialog: React.FC<EditProfileDialogProps> = ({
  open,
  onOpenChange,
  profile,
  onSave,
}) => {
  const { t } = useTranslation("profile");
  const [form, setForm] = React.useState(profile);
  const [errors, setErrors] = React.useState<FormErrors>({});

  React.useEffect(() => {
    setForm(profile);
    setErrors({});
  }, [profile, open]);

  const handleChange = (key: keyof Profile, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const handleEmailChange = (value: string) => {
    setForm((prev) => ({ ...prev, email: value }));
    if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      setErrors((prev) => ({ ...prev, email: t("editDialog.emailInvalid") }));
    } else {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handlePhoneChange = (val: string) => {
    setForm((prev) => ({ ...prev, phone: val }));
    if (val && !isValidPhoneNumber(val)) {
      setErrors((prev) => ({
        ...prev,
        phone: t("editDialog.phoneInvalid"),
      }));
    } else {
      setErrors((prev) => ({ ...prev, phone: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) newErrors.name = t("editDialog.nameRequired");
    if (!form.email.trim()) {
      newErrors.email = t("editDialog.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = t("editDialog.emailInvalid");
    }
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      newErrors.phone = t("editDialog.phoneInvalid");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form);
    onOpenChange(false);
  };

  const handleClose = () => {
    setForm(profile);
    setErrors({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl sm:max-w-[650px]"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t("editDialog.title")}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            {t("editDialog.subtitle")}
          </p>
        </DialogHeader>

        <div
          className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
        >
          <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900 border-b pb-2">
              {t("editDialog.personalInfo")}
            </h3>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                {t("editDialog.nameLabel")} <span className="text-destructive-text">*</span>
              </Label>
              <Input
                id="name"
                placeholder={t("editDialog.namePlaceholder")}
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={cn("w-full", errors.name ? "border-destructive" : "")}
              />
              {errors.name && (
                <p className="text-xs text-destructive-text">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                {t("editDialog.emailLabel")} <span className="text-destructive-text">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t("editDialog.emailPlaceholder")}
                value={form.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className={cn(
                  "w-full",
                  errors.email ? "border-destructive" : "",
                )}
              />
              {errors.email && (
                <p className="text-xs text-destructive-text">{errors.email}</p>
              )}
            </div>

            {/* Mobile Number */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">
                {t("editDialog.phoneLabel")}
              </Label>
              <div
                className={cn(
                  "flex h-10 rounded-md border overflow-hidden",
                  errors.phone ? "border-destructive" : "border-input",
                )}
              >
                <PhoneInput
                  id="phone"
                  defaultCountry="IN"
                  international
                  countryCallingCodeEditable={false}
                  placeholder={t("editDialog.phonePlaceholder")}
                  value={form.phone}
                  onChange={(val) => handlePhoneChange(val ?? "")}
                />
              </div>
              {errors.phone && (
                <p className="text-xs text-destructive-text">{errors.phone}</p>
              )}
            </div>

            {/* Role - read-only */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t("editDialog.roleLabel")}</Label>
              <Input
                value={form.role}
                disabled
                className="w-full bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-3 border-t pt-4">
          <Button variant="outline" onClick={handleClose} className="px-6 cursor-pointer">
            {t("editDialog.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            className="px-6 bg-primary hover:bg-primary cursor-pointer text-white"
          >
            {t("editDialog.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
